import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {Button, ToggleSwitch} from '@primer/react'
import {clsx} from 'clsx'
import {useCallback, useState} from 'react'

import type {ActionConfig, ActionType} from '../config-parser'
import {ACTION_METADATA} from '../config-parser'

import styles from './ConfigSections.module.css'
import {TagInput} from './TagInput'

type ActionsSectionProps = {
  actions: ActionConfig[]
  commentEnabled: boolean
  onChange: (actions: ActionConfig[]) => void
  onCommentEnabledChange: (enabled: boolean) => void
}

export function ActionsSection({actions, commentEnabled, onChange, onCommentEnabledChange}: ActionsSectionProps) {
  const [expandedAction, setExpandedAction] = useState<ActionType | null>(
    () =>
      actions.find(a => a.enabled && ((a.allowed && a.allowed.length > 0) || (a.blocked && a.blocked.length > 0)))
        ?.type ?? null,
  )

  const toggleAction = useCallback(
    (type: ActionType) => {
      onChange(
        actions.map(a => {
          if (a.type !== type) return a
          const nowEnabled = !a.enabled
          // Clear allowed/blocked when disabling
          return nowEnabled ? {...a, enabled: true} : {...a, enabled: false, allowed: undefined, blocked: undefined}
        }),
      )
      const action = actions.find(a => a.type === type)
      if (action?.enabled) {
        setExpandedAction(prev => (prev === type ? null : prev))
      }
    },
    [actions, onChange],
  )

  const updateAction = useCallback(
    (type: ActionType, updates: Partial<ActionConfig>) => {
      onChange(actions.map(a => (a.type === type ? {...a, ...updates} : a)))
    },
    [actions, onChange],
  )

  const toggleAdvanced = useCallback((type: ActionType) => {
    setExpandedAction(prev => (prev === type ? null : type))
  }, [])

  const anyEnabled = commentEnabled || actions.some(a => a.enabled)

  return (
    <div className={styles.section}>
      <div className={styles.sectionHeaderRow}>
        <div className={styles.sectionHeaderText}>
          <h3 className={styles.sectionTitle}>Actions</h3>
          <p className={styles.sectionSubtitle}>
            Configure the actions the agent can take.
            {!anyEnabled && <span className={styles.validationInline}> Select at least one action.</span>}
          </p>
        </div>
      </div>

      <div className={styles.cardList}>
        {/* Post comment — toggleable */}
        <div className={styles.actionCardRow} data-testid="action-post-comment">
          <div className={clsx(styles.cardContent, styles.cardContentInline)}>
            <p className={styles.cardTitle} id="action-label-add-comment">
              Post comment
            </p>
            <p className={styles.cardDescription}>Post comments on issues</p>
          </div>
          <div className={styles.cardActions}>
            <ToggleSwitch
              checked={commentEnabled}
              onClick={() => onCommentEnabledChange(!commentEnabled)}
              aria-labelledby="action-label-add-comment"
              size="small"
            />
          </div>
        </div>

        {/* Configurable actions */}
        {actions.map(action => {
          const meta = ACTION_METADATA[action.type]
          const isExpanded = expandedAction === action.type

          return (
            <div key={action.type} className={styles.actionCardRow} data-testid={`action-${action.type}`}>
              <div className={clsx(styles.cardContent, styles.cardContentInline)}>
                <p className={styles.cardTitle} id={`action-label-${action.type}`}>
                  {meta.label}
                </p>
                <p className={styles.cardDescription}>{meta.description}</p>
              </div>
              <div className={styles.cardActions}>
                {action.enabled && (
                  <Button
                    variant="invisible"
                    size="small"
                    onClick={() => toggleAdvanced(action.type)}
                    aria-expanded={isExpanded}
                    data-testid={`action-advanced-${action.type}`}
                    trailingVisual={isExpanded ? ChevronDownIcon : ChevronRightIcon}
                  >
                    Advanced
                  </Button>
                )}
                <ToggleSwitch
                  checked={action.enabled}
                  onClick={() => toggleAction(action.type)}
                  aria-labelledby={`action-label-${action.type}`}
                  size="small"
                />
              </div>
              {action.enabled && (
                <div
                  className={`${styles.advancedContent} ${isExpanded ? styles.advancedContentOpen : styles.advancedContentClosed}`}
                >
                  <div className={styles.advancedContentInner}>
                    {meta.allowedLabel && (
                      <div>
                        <p className={styles.fieldLabel}>{meta.allowedLabel}</p>
                        <TagInput
                          values={action.allowed ?? []}
                          onChange={values => updateAction(action.type, {allowed: values})}
                          variant="allowed"
                          aria-label={meta.allowedLabel}
                        />
                      </div>
                    )}
                    {meta.blockedLabel && (
                      <div>
                        <p className={styles.fieldLabel}>{meta.blockedLabel}</p>
                        <TagInput
                          values={action.blocked ?? []}
                          onChange={values => updateAction(action.type, {blocked: values})}
                          variant="blocked"
                          aria-label={meta.blockedLabel}
                        />
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}
